import { useEffect, useMemo } from 'react';
import { DocumentIcon, PhotoIcon } from '@heroicons/react/24/outline';
import Loading from 'src/components/Loading';
import EyeIcon from 'src/components/ui-icons/reports/EyeIcon';
import { DownloadIcon } from 'src/components/ui-icons/upload';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { isPreviewable } from 'src/hooks/useFilePreview';
import DeleteIcon from 'src/pages/DashboardPage/gos/ui-icons/deleteIcon';
import { FlexCol, FlexRow } from 'src/utils/flexLayouts';
import { deleteInconsistencyFile, fetchInconsistencyFiles } from '../store/inconsistency5SFiles/actions';
import { selectInconsistencyFiles } from '../store/inconsistency5SFiles/selector';
import { InconsistencyFile } from '../types/inconsistency5SFiles';
import { isImageUrl5S, useFilePreview5SMobile } from './useFilePreview5S.mobile';

type Props = {
  inconsistencyId: number;
  inconsistencyDocumentId: string;
  readOnly?: boolean;
};

const Inconsistency5SFilesMobile = ({ inconsistencyId, inconsistencyDocumentId, readOnly = false }: Props) => {
  const dispatch = useAppDispatch();
  const filesSelector = useMemo(() => selectInconsistencyFiles(inconsistencyId), [inconsistencyId]);
  const files = useAppSelector(filesSelector);
  const { openPreview, previewLoadingId, downloadFile, downloadingUrl, PreviewModal } = useFilePreview5SMobile();

  useEffect(() => {
    dispatch(fetchInconsistencyFiles(inconsistencyId, inconsistencyDocumentId));
  }, [dispatch, inconsistencyId, inconsistencyDocumentId]);

  const handleDelete = (row: InconsistencyFile) => {
    if (!row.recordDocumentId) return;
    dispatch(deleteInconsistencyFile(inconsistencyId, row.recordDocumentId, row.fileId));
  };

  if (files.length === 0) return null;

  return (
    <FlexCol className='gap-2'>
      {files.map((row) => (
        <FlexRow
          key={row.recordDocumentId || row.tempId}
          className='items-center justify-between p-3 bg-white rounded-lg'
        >
          <FlexRow className='items-center flex-1 min-w-0 mr-3'>
            {isImageUrl5S(row.url || row.name) ? (
              <PhotoIcon className='w-5 h-5 text-[#637381] mr-2 shrink-0' />
            ) : (
              <DocumentIcon className='w-5 h-5 text-[#637381] mr-2 shrink-0' />
            )}
            <span className='truncate text-sm text-[#1F2A37]'>{row.name}</span>
            {row.isUploading && <span className='text-xs text-gray-500 ml-2 shrink-0'>загрузка…</span>}
          </FlexRow>
          <FlexRow className='items-center gap-2 shrink-0'>
            {!row.isUploading && isPreviewable(row.url) && (
              <button
                type='button'
                onClick={() => openPreview(row.url, row.fileId)}
                className='p-1'
                title='Предпросмотр'
              >
                {previewLoadingId === row.fileId ? (
                  <FlexCol className='w-6 h-6'>
                    <Loading />
                  </FlexCol>
                ) : (
                  <EyeIcon />
                )}
              </button>
            )}
            {!row.isUploading && row.url && (
              <button
                type='button'
                onClick={() => downloadFile(row.url, row.name)}
                className='p-1'
                title='Скачать'
              >
                {downloadingUrl === row.url ? (
                  <FlexCol className='w-6 h-6'>
                    <Loading />
                  </FlexCol>
                ) : (
                  <DownloadIcon />
                )}
              </button>
            )}
            {!readOnly && (
              <button
                type='button'
                onClick={() => handleDelete(row)}
                className='p-1 disabled:opacity-40'
                title='Удалить'
                disabled={row.isUploading}
              >
                <DeleteIcon />
              </button>
            )}
          </FlexRow>
        </FlexRow>
      ))}
      {PreviewModal}
    </FlexCol>
  );
};

export default Inconsistency5SFilesMobile;
